import {
  inject,
  onMounted,
  ref,
  watch,
} from '@vue/composition-api';

import { initOfferConversion } from '@/plugins/offerConversion';

export default (props, { root }) => {
  const productName = inject('productName');
  const apiProductName = inject('apiProductName');
  const productVersion = inject('productVersion');
  const config = inject('config');

  // refs
  const header = ref(null); // header dom element
  const fixedTopIndent = ref(16); // Default header padding

  const showBespokeFooterInformation = config.bespokeFooterInformation && config.bespokeFooterInformation !== '<br>';

  /**
   * gtag() is a method that sends event data to all of Google measurement products.
   * Needs for GTM initial setup by setting default parameters including consent flags
   * https://developers.google.com/tag-platform/gtagjs/reference
   */
  const gtag = function () {
    window.dataLayer = window.dataLayer || [];
    // eslint-disable-next-line no-undef
    window.dataLayer.push(arguments);
  };
  const gtmTracking = async (step) => {
    const payload = { productName: apiProductName, productVersion };
    if (step) payload.step = step;
    if (
      ['car'].includes(productName) &&
      step !== 'results'
    ) {
      const activeExperiments = getLocalStorageActiveGTMExperiments();
      if (activeExperiments.length) payload.GTMExperiments = activeExperiments;
    };

    try {
      const tracking = await root.$api.getTracking(payload);
      if (tracking.status === 'success') {
        const { data } = tracking;
        if (data?.GTMHeader?.fields) {
          if (data?.GTMHeader?.fields?.GTMCode) delete data.GTMHeader.fields.GTMCode;
          // add tracking data to datalayer
          window.dataLayer = window.dataLayer || [];
          // if gtm is enabled then specify event as a virtual page view event to run appropriate trigger
          if (root.$gtm && root.$gtm.enabled() && !data?.GTMHeader?.fields?.event) data.GTMHeader.fields.event = 'virtualPageView';
          window.dataLayer.push(data.GTMHeader.fields);
          // check if gtm has been injected to header, if not enable it
          // no event need as current triggers are based on gtm.js all pages trigger
          if (root.$gtm && !root.$gtm.enabled()) {
            gtag('consent', 'default', {
              ad_storage: 'granted',
              ad_user_data: 'granted',
              ad_personalization: 'granted',
              analytics_storage: 'granted',
            });
            gtag('js', new Date());
            gtag('config', config.gtmCode);
            root.$gtm.enable(true);
          }
        }

        if (data?.OfferConversion?.active) {
          initOfferConversion();
        }
      }
    } catch (error) {
      if (typeof (error) !== 'undefined') {
        console.error(error.toString());
      }
      console.error('Could not retrieve the tracking code');
    }
  };

  const gtmTrackEvent = (eventType, props) => {
    // disabled for dev
    if (!root.$gtm || !root.$gtm.enabled()) return;

    root.$gtm.trackEvent({
      event: eventType,
      ...props,
    });
  };

  const clearLocalStorage = () => {
    const selectedOptions = root.$ls.get(productName + '__selected-options');

    if (selectedOptions && Object.keys(selectedOptions).length) {
      root.$ls.set(productName + '__selected-options', {});
    }
  };

  onMounted(() => {
    const unwatchHeader = watch(header, () => {
      if (header?.value?.$children[0]?.computedHeight) {
        fixedTopIndent.value += header.value.$children[0].computedHeight;
        unwatchHeader && unwatchHeader();
      }
    }, {
      immediate: true,
    });
  });

  const getLocalStorageActiveGTMExperiments = () => {
    const filterExpiredGTMExperiments = ({ timestamp }) => {
      const EXPIRY_DAYS = 30;
      return (new Date().getTime() - timestamp) < EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    };

    const localStorageExperiments = root.$ls.get('GTMExperiments');
    const activeExperiments = localStorageExperiments?.filter(filterExpiredGTMExperiments) || [];

    return activeExperiments || [];
  };

  return {
    gtmTracking,
    gtmTrackEvent,
    clearLocalStorage,
    productName,

    // other
    fixedTopIndent,
    header,
    showBespokeFooterInformation,
  };
};
